// One place the seller fills in everything the features cannot work out.
//
// Ordered by consequence rather than by feature. A target ACOS changes every
// verdict on every screen and takes ten seconds; product costs unlock one
// sub-feature and take an afternoon. Telling a seller those are the same job is
// how a setup page gets abandoned half-done.
//
// Every field says what it turns on, and every empty one says what happens
// while it stays empty — because "target ACOS: not set" gives a seller no way to
// know it is quietly changing every number they are reading.
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiAlertTriangle, FiArrowLeft, FiCheck, FiLink } from 'react-icons/fi';

import shell from '../SellerIntelligence.module.css';
import s from './AdsSetup.module.css';
import AuthContext from '../../../context/AuthContext';
import { NotConnectedError, getSetup, saveSetup } from './api';

const money = (v) => (v == null ? '' : Number(v).toLocaleString('en-IN'));
// Stored as a fraction, read and written as a percentage — which is how every
// seller and every agency talks about ACOS.
const asPercent = (v) => (v == null ? '' : String(Math.round(v * 1000) / 10));

export default function AdsSetup() {
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);

  const [setup, setSetup] = useState(null);
  const [draft, setDraft] = useState({});
  const [costs, setCosts] = useState({});
  const [brandAdd, setBrandAdd] = useState('');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [refused, setRefused] = useState([]);
  const [notConnected, setNotConnected] = useState(null);

  const fail = useCallback((e) => {
    if (e instanceof NotConnectedError) setNotConnected(e.message);
    else setError(e.message);
  }, []);

  const load = useCallback(() => {
    if (!token) return;
    setLoading(true);
    getSetup(token)
      .then((r) => { setSetup(r); setDraft({}); setCosts({}); setNotConnected(null); })
      .catch(fail)
      .finally(() => setLoading(false));
  }, [token, fail]);

  useEffect(load, [load]);

  const set = (name, value) => setDraft((prev) => ({ ...prev, [name]: value }));
  const dirty = Object.keys(draft).length > 0 || Object.keys(costs).length > 0
    || brandAdd.trim().length > 0;

  const save = async () => {
    setBusy(true); setError(null); setNotice(null); setRefused([]);
    try {
      const body = { ...draft };
      const costRows = Object.entries(costs)
        .filter(([, value]) => value !== '' && value != null)
        .map(([asin, value]) => ({ asin, cost: Number(value) }));
      if (costRows.length) body.costs = costRows;
      if (brandAdd.trim()) body.brands_add = [brandAdd.trim()];
      const r = await saveSetup(token, body);
      setRefused(r.refused || []);
      const saved = Object.keys(r.saved || {});
      setNotice(saved.length
        ? `Saved: ${saved.join(', ')}. ${r.note}`
        : 'Nothing to save.');
      setBrandAdd('');
      load();
    } catch (e) { fail(e); } finally { setBusy(false); }
  };

  const removeBrand = async (token_) => {
    setBusy(true);
    try {
      await saveSetup(token, { brands_remove: [token_] });
      load();
    } catch (e) { fail(e); } finally { setBusy(false); }
  };

  const toggleGrowth = (campaignId, on) => {
    const current = new Set(draft.growth_marked
      || setup?.budget_settings?.growth_marked || []);
    if (on) current.add(campaignId); else current.delete(campaignId);
    set('growth_marked', Array.from(current));
  };

  function section(block) {
    const done = block.done;
    return (
      <section key={block.key} className={`${s.section} ${done ? s.section_done : ''}`}>
        <div className={s.sectionHead}>
          <span className={s.sectionTitleRow}>{block.title}</span>
          <span className={s.takes}>takes {block.takes}</span>
          <span className={done ? s.doneTick : s.todoTick}>
            {done ? <><FiCheck /> done</> : 'needed'}
          </span>
        </div>

        {block.why && <p className={s.why}>{block.why}</p>}

        {!!(block.fields || []).length && (
          <div className={s.fieldRow}>
            {block.fields.map((field) => (
              <div key={field.name} className={s.fieldBox}>
                <label className={s.fieldLabel} htmlFor={field.name}>
                  {field.label}
                  {field.optional && (
                    <span className={s.fieldOptional}> · optional</span>
                  )}
                </label>
                <div className={s.inputWrap}>
                  {field.unit === '₹' && <span className={s.unit}>₹</span>}
                  <input id={field.name} className={s.setupInput} inputMode="decimal"
                         placeholder={field.placeholder}
                         value={draft[field.name] !== undefined
                           ? draft[field.name]
                           : field.unit === '%' ? asPercent(field.value)
                             : money(field.value)}
                         onChange={(e) => set(field.name,
                           e.target.value.replace(/[^0-9.]/g, ''))} />
                  {field.unit === '%' && <span className={s.unit}>%</span>}
                </div>
                <p className={s.fieldWhy}>{field.why}</p>
              </div>
            ))}
          </div>
        )}

        {(block.toggles || []).map((toggle) => (
          <div key={toggle.name} className={s.toggleRow}>
            <input type="checkbox" id={toggle.name}
                   checked={draft[toggle.name] !== undefined
                     ? draft[toggle.name] : toggle.value}
                   onChange={(e) => set(toggle.name, e.target.checked)} />
            <div>
              <label className={s.toggleLabel} htmlFor={toggle.name}>
                {toggle.label}
              </label>
              <p className={s.toggleWhy}>{toggle.why}</p>
            </div>
          </div>
        ))}

        {block.key === 'brands' && (
          <>
            <div className={s.brandRow}>
              {(block.value || []).map((brand) => (
                <span key={brand} className={s.wordChip}>
                  {brand}
                  <button className={s.chipRemove} disabled={busy}
                          onClick={() => removeBrand(brand)}
                          aria-label={`remove ${brand}`}>×</button>
                </span>
              ))}
              {!(block.value || []).length && (
                <span className={s.thin}>Nothing detected yet.</span>
              )}
            </div>
            <div className={s.fieldRow}>
              <div className={s.fieldBox}>
                <label className={s.fieldLabel} htmlFor="brandAdd">
                  Add a brand term
                </label>
                <input id="brandAdd" className={s.setupInput} value={brandAdd}
                       onChange={(e) => setBrandAdd(e.target.value)} />
              </div>
            </div>
          </>
        )}

        {block.key === 'costs' && (
          <>
            <p className={s.whileEmpty}>{block.while_empty}</p>
            <p className={s.why}>
              {block.have} of {block.wanted} products have a cost.
            </p>
            {!!(setup?.amazon_supplies) && (
              <div className={s.amazonGives}>
                {Object.entries(setup.amazon_supplies).map(([name, from]) => (
                  <div key={name} className={s.gives}>
                    <div className={s.givesName}>
                      {name.replace(/_/g, ' ')}
                    </div>
                    <div className={`${s.givesFrom} ${name === 'cost' ? s.givesNone : ''}`}>
                      {from}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className={`${s.tableWrap} ${s.costTable}`}>
              <table className={s.table}>
                <thead>
                  <tr>
                    <th>Product</th><th className={s.right}>Your price</th>
                    <th className={s.right}>Amazon's fees</th>
                    <th className={s.right}>Return rate</th>
                    <th className={s.right}>What it cost you</th>
                  </tr>
                </thead>
                <tbody>
                  {(block.products ? setup.products : []).map((product) => (
                    <tr key={product.asin}>
                      <td>
                        <span className={s.asin}>{product.asin}</span>
                        {product.title && (
                          <div className={s.thin}>
                            {String(product.title).slice(0, 52)}
                          </div>
                        )}
                        {product.advertised && (
                          <div className={s.thin}>advertised</div>
                        )}
                      </td>
                      <td className={s.right}>
                        {product.price != null ? `₹${money(product.price)}` : '—'}
                      </td>
                      <td className={s.right}>
                        {product.fees != null ? `₹${money(product.fees)}` : '—'}
                      </td>
                      <td className={s.right}>
                        {product.return_rate != null
                          ? `${(product.return_rate * 100).toFixed(1)}%` : '—'}
                      </td>
                      <td className={s.right}>
                        <div className={s.inputWrap} style={{ justifyContent: 'flex-end' }}>
                          <span className={s.unit}>₹</span>
                          <input className={s.setupInput} inputMode="decimal"
                                 style={{ width: 90 }}
                                 placeholder={product.cost != null
                                   ? money(product.cost) : ''}
                                 value={costs[product.asin] !== undefined
                                   ? costs[product.asin]
                                   : (product.cost != null
                                     ? money(product.cost) : '')}
                                 onChange={(e) => setCosts((prev) => ({
                                   ...prev,
                                   [product.asin]: e.target.value
                                     .replace(/[^0-9.]/g, ''),
                                 }))} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {block.key === 'campaigns' && (
          <div className={s.tableWrap}>
            <table className={s.table}>
              <thead>
                <tr>
                  <th>Campaign</th><th className={s.right}>Budget a day</th>
                  <th className={s.right}>Its own target</th><th>Leave alone</th>
                </tr>
              </thead>
              <tbody>
                {(block.campaigns || []).map((campaign) => (
                  <tr key={campaign.campaign_id}>
                    <td>{campaign.name}</td>
                    <td className={s.right}>
                      {campaign.budget != null ? `₹${money(campaign.budget)}` : '—'}
                    </td>
                    <td className={s.right}>
                      <div className={s.inputWrap} style={{ justifyContent: 'flex-end' }}>
                        <input className={s.setupInput} inputMode="decimal"
                               style={{ width: 70 }} placeholder="—"
                               value={(draft.target_acos_by_campaign || {})[
                                 campaign.campaign_id] !== undefined
                                 ? draft.target_acos_by_campaign[campaign.campaign_id]
                                 : asPercent(campaign.target_acos)}
                               onChange={(e) => set('target_acos_by_campaign', {
                                 ...(draft.target_acos_by_campaign || {}),
                                 [campaign.campaign_id]: e.target.value
                                   .replace(/[^0-9.]/g, ''),
                               })} />
                        <span className={s.unit}>%</span>
                      </div>
                    </td>
                    <td>
                      <input type="checkbox"
                             checked={(draft.growth_marked
                               || setup?.budget_settings?.growth_marked || [])
                               .includes(campaign.campaign_id)}
                             onChange={(e) => toggleGrowth(campaign.campaign_id,
                               e.target.checked)} />
                    </td>
                  </tr>
                ))}
                {!(block.campaigns || []).length && (
                  <tr>
                    <td colSpan={4} className={s.empty}>
                      No campaigns have been read for this account yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {block.risk && <p className={s.risk}>{block.risk}</p>}
        {block.action && <p className={s.whileEmpty}>{block.action}</p>}
        {!!(block.unlocks || []).length && (
          <ul className={s.unlocks}>
            {block.unlocks.map((line) => <li key={line}>unlocks: {line}</li>)}
          </ul>
        )}
      </section>
    );
  }

  function body() {
    if (notConnected) {
      return (
        <div className={s.locked}>
          <div className={s.lockedIcon}><FiLink /></div>
          <h2 className={s.lockedTitle}>Connect Amazon first</h2>
          <p className={s.lockedBody}>{notConnected}</p>
          <button className={s.button}
                  onClick={() => navigate('/dashboard/market-places')}>
            Go to the Amazon card
          </button>
        </div>
      );
    }
    if (loading && !setup) return <p className={s.empty}>Loading…</p>;
    if (!setup) return <p className={s.empty}>{error || 'Nothing to show.'}</p>;

    const needed = (setup.sections || []).filter((block) => !block.done).length;

    return (
      <div className={s.page}>
        {error && <div className={s.error}>{error}</div>}
        {notice && <div className={s.notice}>{notice}</div>}
        {!!refused.length && (
          <div className={s.refused}>
            {refused.map((row) => (
              <div key={row.field}><b>{row.field}</b> — {row.why}</div>
            ))}
          </div>
        )}

        <p className={s.principle}>
          {needed === 0
            ? 'Everything the features need from you is filled in.'
            : `${needed} of ${(setup.sections || []).length} still needed. `}
          Nothing on this page is sent to Amazon — these are your own figures, and they
          only change what the features measure against.
        </p>

        {(setup.sections || []).map(section)}

        <div className={s.saveBar}>
          <button className={s.approve} disabled={busy || !dirty} onClick={save}>
            <FiCheck /> Save
          </button>
          {dirty && (
            <span className={s.dirty}>
              <FiAlertTriangle /> Unsaved changes
            </span>
          )}
          {!dirty && !busy && (
            <span className={s.takes}>Nothing changed.</span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={shell.featureContainer}>
      <div className={shell.header}>
        <button className={shell.backButton}
                onClick={() => navigate('/dashboard/seller-intelligence')}>
          <FiArrowLeft /> Back
        </button>
        <div className={shell.titleSection}>
          <div className={shell.iconTitle}><h1>Ads Setup</h1></div>
          <p>The figures only you know — what each one turns on, and what happens
             while it is empty</p>
        </div>
      </div>
      <div className={shell.content}>{body()}</div>
    </div>
  );
}
